// src/components/modals/VenueProfileSetupModal.jsx
import React from 'react';
import { Check } from 'lucide-react';

const VENUE_TYPES = [
  'Bar / Pub', 'Nightclub', 'Restaurant', 'Concert Hall', 'Rooftop Venue',
  'Outdoor Amphitheatre', 'Hotel Banquet', 'Café / Lounge', 'Private Event Space', 'Other'
];

const VenueProfileSetupModal = ({
  show,
  currentUser,
  emailVerified,
  profileStep,
  setProfileStep,
  profileData,
  setProfileData,
  handleSaveProfile,
  handleSendVerification,
  handleCheckVerification,
  saving,
}) => {
  if (!show) return null;

  const sm = profileData.socialMedia || {};
  const setSM = (key, val) =>
    setProfileData({ ...profileData, socialMedia: { ...sm, [key]: val } });

  return (
    <div className="fixed inset-0 bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="rock-background gig-card border-2 border-electric max-w-3xl w-full my-8">

        {/* Header */}
        <div className="mb-6">
          <h2 className="concert-heading text-4xl text-electric mb-2">
            🏢 COMPLETE YOUR VENUE PROFILE
          </h2>
          <p className="text-gray-light">
            Set up your venue to start hiring artists and receiving bookings!
          </p>

          {/* Progress bar */}
          <div className="mt-4 flex items-center gap-2">
            {[1, 2, 3].map(s => (
              <div key={s}
                className={`flex-1 h-2 rounded-full transition-all ${profileStep >= s ? 'bg-electric' : 'bg-white/20'}`} />
            ))}
          </div>
          <p className="text-electric text-sm mt-2 font-bold">Step {profileStep} of 3</p>
        </div>

        {/* Email verification warning */}
        {!emailVerified && (
          <div className="bg-red-500/20 border-2 border-red-400 rounded-lg p-6 mb-6">
            <p className="text-red-300 font-bold text-lg mb-2">⚠️ EMAIL NOT VERIFIED</p>
            <p className="text-white mb-4">
              You must verify your email before completing your venue profile.
            </p>
            <p className="text-red-200 text-sm mb-4">
              Check your inbox: <span className="font-bold">{currentUser?.email}</span>
            </p>
            <div className="flex gap-3">
              <button onClick={handleSendVerification} className="flex-1 btn btn-neon text-sm">
                📧 Send Verification Email
              </button>
              <button onClick={handleCheckVerification} className="flex-1 btn btn-electric text-sm">
                🔄 Check Status
              </button>
            </div>
          </div>
        )}

        {/* Privacy note */}
        <div className="bg-green-500/20 border border-neon rounded-lg p-4 mb-6">
          <p className="text-neon font-bold text-sm">
            🔒 Your email ({currentUser?.email}) will NOT be displayed publicly and remains confidential.
          </p>
        </div>

        <div className="space-y-6">

          {/* ── STEP 1: Venue Details ── */}
          {profileStep === 1 && (
            <div className="space-y-4">
              <h3 className="concert-heading text-2xl text-magenta mb-4">🏢 VENUE DETAILS</h3>

              <div>
                <label className="text-electric font-bold mb-2 block text-sm">Venue Name *</label>
                <input type="text"
                  value={profileData.venueName || ''}
                  onChange={e => setProfileData({ ...profileData, venueName: e.target.value })}
                  placeholder="e.g. The Blue Note, Hard Rock Café"
                  className="w-full px-4 py-3 rounded-lg bg-white/10 text-white placeholder-white/40 border border-electric/30 focus:border-electric focus:outline-none" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-electric font-bold mb-2 block text-sm">Venue Type *</label>
                  <select
                    value={profileData.venueType || ''}
                    onChange={e => setProfileData({ ...profileData, venueType: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-white/10 text-white border border-electric/30 focus:border-electric focus:outline-none">
                    <option value="" disabled>Select type...</option>
                    {VENUE_TYPES.map(t => (
                      <option key={t} value={t} style={{ background: '#1a1a2e' }}>{t}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-electric font-bold mb-2 block text-sm">Capacity *</label>
                  <input type="number" min="1"
                    value={profileData.capacity || ''}
                    onChange={e => setProfileData({ ...profileData, capacity: e.target.value })}
                    placeholder="e.g. 200"
                    className="w-full px-4 py-3 rounded-lg bg-white/10 text-white placeholder-white/40 border border-electric/30 focus:border-electric focus:outline-none" />
                </div>
              </div>

              <div>
                <label className="text-electric font-bold mb-2 block text-sm">Location / City *</label>
                <input type="text"
                  value={profileData.location || ''}
                  onChange={e => setProfileData({ ...profileData, location: e.target.value })}
                  placeholder="e.g. Mumbai, Maharashtra"
                  className="w-full px-4 py-3 rounded-lg bg-white/10 text-white placeholder-white/40 border border-electric/30 focus:border-electric focus:outline-none" />
              </div>

              <div>
                <label className="text-electric font-bold mb-2 block text-sm">
                  About the Venue * <span className="text-gray-400 font-normal">(min 50 characters)</span>
                </label>
                <textarea
                  value={profileData.description || ''}
                  onChange={e => setProfileData({ ...profileData, description: e.target.value })}
                  placeholder="Tell artists about your venue — the vibe, events you host, what makes it special..."
                  rows={5}
                  className="w-full px-4 py-3 rounded-lg bg-white/10 text-white placeholder-white/40 border border-electric/30 focus:border-electric focus:outline-none resize-none" />
                <p className={`text-sm mt-1 ${(profileData.description || '').length >= 50 ? 'text-neon' : 'text-gray-400'}`}>
                  {(profileData.description || '').length}/50 characters
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-electric font-bold mb-2 block text-sm">Genres You Book *</label>
                  <input type="text"
                    value={profileData.genres || ''}
                    onChange={e => setProfileData({ ...profileData, genres: e.target.value })}
                    placeholder="e.g. Rock, Jazz, Bollywood"
                    className="w-full px-4 py-3 rounded-lg bg-white/10 text-white placeholder-white/40 border border-electric/30 focus:border-electric focus:outline-none" />
                </div>
                <div>
                  <label className="text-electric font-bold mb-2 block text-sm">Typical Budget (₹) *</label>
                  <input type="text"
                    value={profileData.typicalBudget || ''}
                    onChange={e => setProfileData({ ...profileData, typicalBudget: e.target.value })}
                    placeholder="e.g. 5000 – 20000"
                    className="w-full px-4 py-3 rounded-lg bg-white/10 text-white placeholder-white/40 border border-electric/30 focus:border-electric focus:outline-none" />
                </div>
              </div>

              <button
                onClick={() => {
                  if (!profileData.venueName || !profileData.venueType || !profileData.capacity ||
                    !profileData.location || (profileData.description || '').length < 50 ||
                    !profileData.genres || !profileData.typicalBudget) {
                    alert('⚠️ Please fill in all required fields! (Description needs 50+ characters)');
                    return;
                  }
                  setProfileStep(2);
                }}
                className="btn btn-neon w-full text-lg">
                Next: Contact & Socials →
              </button>
            </div>
          )}

          {/* ── STEP 2: Contact & Online Presence ── */}
          {profileStep === 2 && (
            <div className="space-y-4">
              <h3 className="concert-heading text-2xl text-magenta mb-4">📞 CONTACT & ONLINE PRESENCE</h3>
              <p className="text-gray-light text-sm mb-4">
                Help artists find and verify your venue — and reach out for bookings.
              </p>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-electric font-bold mb-2 block text-sm">📞 Phone *</label>
                  <input type="tel"
                    value={profileData.phone || ''}
                    onChange={e => setProfileData({ ...profileData, phone: e.target.value })}
                    placeholder="+91 98765 43210"
                    className="w-full px-4 py-3 rounded-lg bg-white/10 text-white placeholder-white/40 border border-electric/30 focus:border-electric focus:outline-none" />
                </div>
                <div>
                  <label className="text-electric font-bold mb-2 block text-sm">📧 Booking Email *</label>
                  <input type="email"
                    value={profileData.contactEmail || ''}
                    onChange={e => setProfileData({ ...profileData, contactEmail: e.target.value })}
                    placeholder="bookings@yourvenue.com"
                    className="w-full px-4 py-3 rounded-lg bg-white/10 text-white placeholder-white/40 border border-electric/30 focus:border-electric focus:outline-none" />
                </div>
              </div>

              <div>
                <label className="text-electric font-bold mb-2 block text-sm">🌐 Website</label>
                <input type="url"
                  value={profileData.website || ''}
                  onChange={e => setProfileData({ ...profileData, website: e.target.value })}
                  placeholder="https://yourvenue.com"
                  className="w-full px-4 py-3 rounded-lg bg-white/10 text-white placeholder-white/40 border border-electric/30 focus:border-electric focus:outline-none" />
              </div>

              <div>
                <label className="text-electric font-bold mb-2 block text-sm">📷 Instagram</label>
                <input type="text"
                  value={sm.instagram || ''}
                  onChange={e => setSM('instagram', e.target.value)}
                  placeholder="@venuename or full URL"
                  className="w-full px-4 py-3 rounded-lg bg-white/10 text-white placeholder-white/40 border border-electric/30 focus:border-electric focus:outline-none" />
              </div>

              <div>
                <label className="text-electric font-bold mb-2 block text-sm">👍 Facebook</label>
                <input type="text"
                  value={sm.facebook || ''}
                  onChange={e => setSM('facebook', e.target.value)}
                  placeholder="Page URL"
                  className="w-full px-4 py-3 rounded-lg bg-white/10 text-white placeholder-white/40 border border-electric/30 focus:border-electric focus:outline-none" />
              </div>

              <div>
                <label className="text-electric font-bold mb-2 block text-sm">💼 LinkedIn</label>
                <input type="text"
                  value={sm.linkedin || ''}
                  onChange={e => setSM('linkedin', e.target.value)}
                  placeholder="https://linkedin.com/company/yourvenue"
                  className="w-full px-4 py-3 rounded-lg bg-white/10 text-white placeholder-white/40 border border-electric/30 focus:border-electric focus:outline-none" />
              </div>

              <div className="flex gap-3 pt-2">
                <button onClick={() => setProfileStep(1)} className="btn btn-ghost">← Back</button>
                <button
                  onClick={() => {
                    if (!profileData.phone || !profileData.contactEmail) {
                      alert('⚠️ Phone and booking email are required!'); return;
                    }
                    setProfileStep(3);
                  }}
                  className="flex-1 btn btn-neon text-lg">
                  Next: Review →
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 3: Review & Save ── */}
          {profileStep === 3 && (
            <div className="space-y-4">
              <h3 className="concert-heading text-2xl text-magenta mb-4">✅ REVIEW & SAVE</h3>

              <div className="bg-white/5 rounded-lg p-6 space-y-4">

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-gray-400 text-sm">Venue Name</p>
                    <p className="text-white font-bold text-lg">{profileData.venueName}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Venue Type</p>
                    <p className="text-electric font-bold">{profileData.venueType}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-gray-400 text-sm">Capacity</p>
                    <p className="text-white font-bold">{profileData.capacity} people</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Location</p>
                    <p className="text-electric font-bold">📍 {profileData.location}</p>
                  </div>
                </div>

                <div>
                  <p className="text-gray-400 text-sm">About</p>
                  <p className="text-white">{profileData.description}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-gray-400 text-sm">Genres</p>
                    <p className="text-neon font-bold">🎸 {profileData.genres}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Typical Budget</p>
                    <p className="text-neon font-bold">💰 ₹{profileData.typicalBudget}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-gray-400 text-sm">Phone</p>
                    <p className="text-white font-bold">📞 {profileData.phone}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Booking Email</p>
                    <p className="text-white font-bold">📧 {profileData.contactEmail}</p>
                  </div>
                </div>

                {profileData.website && (
                  <div>
                    <p className="text-gray-400 text-sm">Website</p>
                    <p className="text-electric font-bold">🌐 {profileData.website}</p>
                  </div>
                )}

                {Object.values(sm).some(v => v) && (
                  <div>
                    <p className="text-gray-400 text-sm mb-2">Social Media</p>
                    <div className="flex flex-wrap gap-2">
                      {sm.instagram && <span className="px-3 py-1 bg-pink-500/20 text-pink-300 rounded-full text-sm">📷 Instagram</span>}
                      {sm.facebook && <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm">👍 Facebook</span>}
                      {sm.linkedin && <span className="px-3 py-1 bg-blue-700/20 text-blue-400 rounded-full text-sm">💼 LinkedIn</span>}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <button onClick={() => setProfileStep(2)} className="btn btn-ghost">← Back</button>
                <button onClick={handleSaveProfile} disabled={saving} className="flex-1 btn btn-neon text-lg">
                  <Check size={20} />
                  <span>{saving ? '⏳ Saving...' : 'Save Venue Profile & Continue'}</span>
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default VenueProfileSetupModal;
